// # Amboss Question Bank Component for Step 1
import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../../auth/state';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { Lock, Edit, Save, Cancel, Shuffle } from '@mui/icons-material';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend 
} from 'recharts';
import './amboss1.css';
import { 
  FaCalendarAlt, 
  FaClock,
  FaBook,
  FaClipboardCheck,
  FaChartLine,
  FaTasks,
  FaUserClock,
  FaListAlt,
  FaUserMd,
  FaExclamationTriangle,
  FaCalendarCheck,
  FaRegCircle,
  FaExclamationCircle,
  FaChevronDown
} from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';

// # Import Firebase
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';

// # Import role access utility
import m1Access from '../m1_access';
import cacheManager from '../../../auth/cache_manager';

// # Define chart colors
const CHART_COLORS = ['#3498db', '#2ecc71', '#e74c3c', '#f1c40f', '#9b59b6'];

// # Define status options
const STATUS_OPTIONS = ['Not Started', 'In Progress', 'Completed'];

// # Define systems for step 1
const SYSTEMS = [
  "Allergy & Immunology",
  "Behavioral Science",
  "Biostatistics & Epidemiology",
  "Cardiovascular",
  "Dermatology",
  "Drug Advertising & Prescribing",
  "Emergency Medicine",
  "Endocrine, Diabetes & Metabolism",
  "Ethics & Legal",
  "Female Reproductive & Breast",
  "Gastrointestinal & Nutrition",
  "General Principles",
  "Geriatrics",
  "Health & Healthcare Systems",
  "Hematology & Oncology",
  "Infectious Diseases",
  "Male Reproductive",
  "Musculoskeletal, Skin & Connective Tissue",
  "Nervous System & Special Senses",
  "Patient Safety & Quality Improvement",
  "Pediatrics",
  "Pregnancy, Childbirth & Puerperium",
  "Preventive Medicine & Health Maintenance",
  "Psychiatry",
  "Pulmonary & Critical Care",
  "Renal, Urinary Systems & Electrolytes",
  "Surgery",
  "Random" // # Added Random chapter
];

function AmbossQBank({ isAdmin, isTutor, isMentorView, menteeId }) {
  const navigate = useNavigate();
  const { username } = useParams(); // # Get mentee username from URL if in mentor view
  const { userProfile, user } = useAuthStore();
  const [qbankData, setQbankData] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [tempData, setTempData] = useState({});
  const [selectedChart, setSelectedChart] = useState('bar');
  const [targetUserId, setTargetUserId] = useState(null);
  const [isMentor, setIsMentor] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Access is always granted for admin/tutor, subscription check is handled by ProtectedRouteUser
  const stepAccess = isAdmin || isTutor ? true : true;

  // # Get the correct user ID based on role context
  useEffect(() => {
    // # Use the access utility to determine the target user ID
    const userId = m1Access.getTargetUserId({
      isAdmin,
      isTutor,
      viewingMenteeId: menteeId || sessionStorage.getItem('currentViewingMenteeId'),
      currentUserId: user?.uid
    });
    
    setTargetUserId(userId);
    setIsMentor(userId !== user?.uid);
    
    // # Determine if current user can edit content
    setCanEdit(m1Access.canEditContent({
      isAdmin,
      isTutor,
      isMentorView
    }) || userId === user?.uid);
  }, [user?.uid, isAdmin, isTutor, isMentorView, menteeId]);

  // # Load data from Firestore
  useEffect(() => {
    const loadQBankData = async () => {
      if (!targetUserId) return;

      try {
        // # Only load data if we have access
        if (stepAccess) {
          const docRef = doc(db, 's1_mentee', targetUserId);
          const docSnap = await getDoc(docRef);
          const ambossData = docSnap.data()?.amboss || {};

          // # Initialize with loaded data or default values
          const initialData = SYSTEMS.reduce((acc, system) => {
            acc[system] = {
              status: ambossData[system]?.status || 'Not Started',
              percentage: ambossData[system]?.percentage || 0,
              correct: ambossData[system]?.correct || 0,
              incorrect: ambossData[system]?.incorrect || 0,
              weaknesses: ambossData[system]?.weaknesses || []
            };
            return acc;
          }, {});

          setQbankData(initialData);
        }
      } catch (error) {
        console.error('Error loading qbank data:', error);
      }
    };

    loadQBankData();
  }, [targetUserId, stepAccess]);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Calculate statistics
  const calculateStats = (stepData) => {
    if (!stepData) return {
      totalCompleted: 0,
      totalChapters: 0,
      totalCorrect: 0,
      totalIncorrect: 0,
      totalWeakPoints: 0,
      totalPercentage: 0,
      averagePercentage: 0
    };

    // Filter out the Random system from calculations
    const systems = Object.entries(stepData).filter(([system]) => system !== "Random");
    const totalCompleted = systems.filter(([_, sys]) => sys?.status === 'Completed').length;
    const totalChapters = systems.length;
    const totalCorrect = systems.reduce((sum, [_, sys]) => sum + (sys?.correct || 0), 0);
    const totalIncorrect = systems.reduce((sum, [_, sys]) => sum + (sys?.incorrect || 0), 0);
    const totalWeakPoints = systems.reduce((sum, [_, sys]) => sum + ((sys?.weaknesses || []).length || 0), 0);
    const totalPercentage = totalCorrect + totalIncorrect > 0 
      ? ((totalCorrect / (totalCorrect + totalIncorrect)) * 100).toFixed(1)
      : 0;
    const averagePercentage = systems.length > 0 
      ? systems.reduce((sum, [_, sys]) => {
          const total = (sys?.correct || 0) + (sys?.incorrect || 0);
          return sum + (total > 0 ? ((sys?.correct || 0) / total * 100) : 0);
        }, 0) / systems.length 
      : 0;

    return {
      totalCompleted,
      totalChapters,
      totalCorrect,
      totalIncorrect,
      totalWeakPoints,
      totalPercentage,
      averagePercentage
    };
  };

  // # Get chart data
  const getChartData = (stepData) => {
    return Object.entries(stepData)
      .filter(([system]) => system !== "Random") // Exclude Random from visualization
      .map(([system, data]) => ({
        name: system.split(' ')[0], // Use first word for shorter labels
        percentage: data.percentage || 0,
        correct: data.correct || 0,
        incorrect: data.incorrect || 0
      }));
  };

  // # Toggle edit mode - only allow if user can edit
  const toggleEditMode = () => {
    if (!canEdit) return;
    
    if (editMode) {
      // # Cancel edit mode
      setEditMode(false);
    } else {
      // # Enter edit mode and create a copy of the current data
      setTempData(JSON.parse(JSON.stringify(qbankData)));
      setEditMode(true);
    }
  };

  // # Handle input changes
  const handleInputChange = (system, field, value) => {
    // # Create a deep copy of the temporary data
    const updatedData = { ...tempData };
    
    // # Update the specific field
    if (field === 'status') {
      updatedData[system].status = value;
    } else if (field === 'percentage') {
      // # Ensure percentage is between 0 and 100
      const numValue = Math.min(100, Math.max(0, parseInt(value) || 0));
      updatedData[system].percentage = numValue;
    } else if (field === 'correct') {
      // # Ensure correct is a non-negative number
      const numValue = Math.max(0, parseInt(value) || 0);
      updatedData[system].correct = numValue;
    } else if (field === 'incorrect') {
      // # Ensure incorrect is a non-negative number
      const numValue = Math.max(0, parseInt(value) || 0);
      updatedData[system].incorrect = numValue;
    } else if (field === 'weakness') {
      // # Add a new weakness
      if (value.trim() !== '') {
        updatedData[system].weaknesses = [
          ...updatedData[system].weaknesses,
          value.trim()
        ];
      }
    } else if (field === 'removeWeakness') {
      // # Remove a weakness by index
      updatedData[system].weaknesses = updatedData[system].weaknesses.filter(
        (_, index) => index !== parseInt(value)
      );
    } else if (field === 'weaknesses') {
      // # Set the weaknesses array directly (used by the UI)
      updatedData[system].weaknesses = value;
    }
    
    // # Update the temporary data
    setTempData(updatedData);
  };

  // # Save changes
  const saveChanges = async () => {
    const calculatedData = { ...tempData };

    // # Calculate for each system
    Object.keys(calculatedData).forEach(system => {
      const data = calculatedData[system];
      
      // # Skip if no numeric values entered
      if (!data.correct && !data.incorrect && !data.percentage) {
        return;
      }

      // # Convert empty strings to 0
      const correct = data.correct === '' ? 0 : Number(data.correct || 0);
      const incorrect = data.incorrect === '' ? 0 : Number(data.incorrect || 0);
      const percentage = data.percentage === '' ? 0 : Number(data.percentage || 0);

      // # Calculate missing values
      if (correct && incorrect) {
        // # Calculate percentage from correct and incorrect
        const total = correct + incorrect;
        calculatedData[system].percentage = total > 0 ? ((correct / total) * 100).toFixed(1) : 0;
      } else if (correct && percentage) {
        // # Calculate incorrect from correct and percentage
        calculatedData[system].incorrect = Math.round((correct * 100 / percentage) - correct);
      } else if (incorrect && percentage) {
        // # Calculate correct from incorrect and percentage
        calculatedData[system].correct = Math.round((incorrect * percentage) / (100 - percentage));
      }

      // # Ensure all values are numbers, not strings
      calculatedData[system].correct = Number(calculatedData[system].correct || 0);
      calculatedData[system].incorrect = Number(calculatedData[system].incorrect || 0);
      calculatedData[system].percentage = Number(calculatedData[system].percentage || 0);
    });

    try {
      // # Update the UI first for better user experience
      setQbankData(calculatedData);
      setEditMode(false);
      
      // # Save to Firestore
      await saveToFirestore(calculatedData);
    } catch (error) {
      console.error('Error saving changes:', error);
      // # Revert to original data if save fails
      setQbankData(qbankData);
    }
  };

  // # Get pie chart data
  const getPieChartData = (stepData) => {
    const stats = calculateStats(stepData);
    // # Calculate total questions attempted
    const totalQuestions = stats.totalCorrect + stats.totalIncorrect;
    
    // # Return correct and incorrect percentages
    return [
      { name: 'Correct', value: stats.totalCorrect, color: '#4CAF50' },
      { name: 'Incorrect', value: stats.totalIncorrect, color: '#F44336' }
    ];
  };

  // # Get line chart data
  const getLineChartData = (stepData) => {
    return Object.entries(stepData)
      .filter(([system]) => system !== "Random") // Exclude Random from visualization
      .map(([system, data]) => ({
        name: system.split(' ')[0], // Use first word for shorter labels
        percentage: data.percentage || 0
      }));
  };

  // # Render chart based on selected type
  const renderChart = (data) => {
    const margin = { top: 20, right: 30, left: 20, bottom: 100 };
    const containerWidth = document.querySelector('.amboss1_chart_container')?.clientWidth || 600;
    const chartWidth = Math.min(Math.max(getChartData(data).length * 80, containerWidth - 40), containerWidth);
    
    switch (selectedChart) {
      case 'bar':
        return (
          <div className="amboss1_chart_scroll_container">
            <BarChart 
              data={getChartData(data)}
              margin={margin}
              width={chartWidth}
              height={300}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45}
                textAnchor="end"
                interval={0}
                height={60}
              />
              <YAxis domain={[0, 100]} />
              <Tooltip />
              <Bar dataKey="percentage" name="Percentage" fill="#3498db" />
            </BarChart>
          </div>
        );
      case 'pie':
        return (
          <div className="amboss1_chart_scroll_container">
            <div className="amboss1_pie_chart_container">
              <PieChart
                width={Math.min(800, containerWidth - 40)}
                height={600}
                margin={{ top: 40, right: 30, left: 30, bottom: 40 }}
              >
                <Pie
                  data={getPieChartData(data)}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="45%"
                  outerRadius={Math.min(180, (containerWidth - 120) / 4)}
                  label={({ name, value, percent }) => 
                    `${name}: ${value} (${(percent * 100).toFixed(1)}%)`
                  }
                >
                  {getPieChartData(data).map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name) => [`Total: ${value}`, name]}
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '8px',
                    padding: '10px 14px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend 
                  layout="horizontal" 
                  verticalAlign="bottom" 
                  align="center"
                  wrapperStyle={{ 
                    paddingTop: '40px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                />
              </PieChart>
            </div>
          </div>
        );
      case 'line':
        return (
          <div className="amboss1_chart_scroll_container">
            <LineChart 
              data={getLineChartData(data)}
              margin={margin}
              width={chartWidth}
              height={300}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45}
                textAnchor="end"
                interval={0}
                height={60}
              />
              <YAxis domain={[0, 100]} />
              <Tooltip />
              <Line type="monotone" dataKey="percentage" stroke="#3498db" />
            </LineChart>
          </div>
        );
      default:
        return null;
    }
  };

  // # Save data to Firestore
  const saveToFirestore = async (data) => {
    if (!targetUserId) return;

    try {
      const docRef = doc(db, 's1_mentee', targetUserId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        // # Update existing document
        await setDoc(docRef, {
          ...docSnap.data(),
          amboss: data
        }, { merge: true });
      } else {
        // # Create new document
        await setDoc(docRef, {
          amboss: data
        });
      }
    } catch (error) {
      console.error('Error saving to Firestore:', error);
      throw error;
    }
  };

  // # Calculate statistics
  const stats = calculateStats(qbankData);
  const isLocked = !stepAccess;

  return (
    <div className={`amboss1_container ${isDarkMode ? 'amboss1_dark_mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <div className="amboss1_main">
        <div className="amboss1_title">
          <FaRegCircle style={{ color: '#3498db', fontSize: '2rem' }} />
          AMBOSS Question Bank
          {isMentor && <span className="amboss1_mentor_pill">Mentor</span>}
        </div>
        
        {/* # Step content */}
        <div className="amboss1_step_section">
          <div className="amboss1_step_header">
            <FaBook className="amboss1_step_icon" />
            <h2>Step 1 AMBOSS</h2>
            
            {!isLocked && canEdit && (
              editMode ? (
                <>
                  <button 
                    className="amboss1_edit_button" 
                    onClick={saveChanges}
                  >
                    <Save fontSize="small" /> Save
                  </button>
                  <button 
                    className="amboss1_cancel_button" 
                    onClick={toggleEditMode}
                  >
                    <Cancel fontSize="small" /> Cancel
                  </button>
                </>
              ) : (
                <button 
                  className="amboss1_edit_button" 
                  onClick={toggleEditMode}
                >
                  <Edit fontSize="small" /> Edit
                </button>
              )
            )}
            
            {isLocked && (
              <div className="amboss1_lock_icon">
                <Lock />
                <span className="amboss1_lock_text">Subscription Required</span>
              </div>
            )}
          </div>
          
          <div className="amboss1_table_container">
            <table className="amboss1_table">
              <thead>
                <tr>
                  <th>System</th>
                  <th>Status</th>
                  <th>Correct %</th>
                  <th>Correct</th>
                  <th>Incorrect</th>
                  <th>Weaknesses</th>
                </tr>
              </thead>
              <tbody>
                {SYSTEMS.map((system) => {
                  // Calculate correct percentage for each system
                  const totalQuestions = (qbankData[system]?.correct || 0) + (qbankData[system]?.incorrect || 0);
                  const correctPercentage = totalQuestions > 0 
                    ? ((qbankData[system]?.correct || 0) / totalQuestions * 100).toFixed(1)
                    : 0;
                  
                  return (
                    <tr key={system} className={system === "Random" ? "amboss1_random_row" : ""}>
                      <td>
                        {system === "Random" ? (
                          <div className="amboss1_system_with_icon">
                            <Shuffle className="amboss1_system_icon" />
                            <span>{system}</span>
                            <div className="amboss1_random_note">
                              <FaExclamationCircle />
                              <span>Excluded from stats</span>
                            </div>
                          </div>
                        ) : (
                          system
                        )}
                      </td>
                      <td>
                        {editMode ? (
                          <select
                            className={`amboss1_status_select amboss1_status_${tempData[system]?.status?.toLowerCase().replace(/\s+/g, '_')}`}
                            value={tempData[system]?.status || 'Not Started'}
                            onChange={(e) => handleInputChange(system, 'status', e.target.value)}
                          >
                            {STATUS_OPTIONS.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className={`amboss1_status_${qbankData[system]?.status?.toLowerCase().replace(/\s+/g, '_')}`}>
                            {qbankData[system]?.status || 'Not Started'}
                          </span>
                        )}
                      </td>
                      <td>
                        {editMode ? (
                          <input
                            type="number"
                            className="amboss1_edit_input"
                            value={tempData[system]?.percentage === 0 ? '' : tempData[system]?.percentage || ''}
                            onChange={(e) => handleInputChange(system, 'percentage', e.target.value)}
                            min="0"
                            max="100"
                          />
                        ) : (
                          `${correctPercentage}%`
                        )}
                      </td>
                      <td>
                        {editMode ? (
                          <input
                            type="number"
                            className="amboss1_edit_input"
                            value={tempData[system]?.correct === 0 ? '' : tempData[system]?.correct || ''}
                            onChange={(e) => handleInputChange(system, 'correct', e.target.value)}
                            min="0"
                          />
                        ) : (
                          qbankData[system]?.correct || 0
                        )}
                      </td>
                      <td>
                        {editMode ? (
                          <input
                            type="number"
                            className="amboss1_edit_input"
                            value={tempData[system]?.incorrect === 0 ? '' : tempData[system]?.incorrect || ''}
                            onChange={(e) => handleInputChange(system, 'incorrect', e.target.value)}
                            min="0"
                          />
                        ) : (
                          qbankData[system]?.incorrect || 0
                        )}
                      </td>
                      <td className="amboss1_weakness_cell">
                        {editMode ? (
                          <div className="amboss1_weakness_edit_container">
                            <div className="amboss1_weakness_list">
                              {tempData[system]?.weaknesses?.length > 0 && (
                                <ul>
                                  {tempData[system]?.weaknesses.map((weakness, index) => (
                                    <li key={index}>
                                      {weakness}
                                      <button
                                        className="amboss1_remove_weakness_btn"
                                        onClick={() => {
                                          const updatedWeaknesses = tempData[system]?.weaknesses.filter((_, i) => i !== index);
                                          handleInputChange(system, 'weaknesses', updatedWeaknesses);
                                        }}
                                      >
                                        ×
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                            <div className="amboss1_weakness_input_container">
                              <input
                                type="text"
                                className="amboss1_edit_input amboss1_weakness_input"
                                placeholder="Type new weakness"
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter' && e.target.value.trim()) {
                                    const currentWeaknesses = tempData[system]?.weaknesses || [];
                                    handleInputChange(
                                      system,
                                      'weaknesses',
                                      [...currentWeaknesses, e.target.value.trim()]
                                    );
                                    e.target.value = '';
                                  }
                                }}
                              />
                              <button 
                                className="amboss1_add_weakness_btn"
                                onClick={(e) => {
                                  const input = e.target.parentElement.querySelector('input');
                                  if (input.value.trim()) {
                                    const currentWeaknesses = tempData[system]?.weaknesses || [];
                                    handleInputChange(
                                      system,
                                      'weaknesses',
                                      [...currentWeaknesses, input.value.trim()]
                                    );
                                    input.value = '';
                                  }
                                }}
                              >
                                +
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="amboss1_weakness_list">
                            {qbankData[system]?.weaknesses?.length > 0 ? (
                              <ul>
                                {qbankData[system]?.weaknesses.map((weakness, index) => (
                                  <li key={index}>{weakness}</li>
                                ))}
                              </ul>
                            ) : (
                              <span>None</span>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* # Dashboard */}
        <div className="amboss1_dashboard">
          <div className="amboss1_dashboard_stats">
            <div className="amboss1_stat_card">
              <h3>Progress</h3>
              <p>{stats.totalCompleted} / {stats.totalChapters}</p>
            </div>
            <div className="amboss1_stat_card">
              <h3>Total Correct</h3>
              <p>{stats.totalCorrect}</p>
            </div>
            <div className="amboss1_stat_card">
              <h3>Total Incorrect</h3>
              <p>{stats.totalIncorrect}</p>
            </div>
            <div className="amboss1_stat_card">
              <h3>Total Percentage</h3>
              <p>{stats.totalPercentage}%</p>
            </div>
          </div>
        </div>
        
        {/* # Charts */}
        <div className="amboss1_charts">
          <div className="amboss1_chart_controls">
            <button
              className={`amboss1_chart_type_btn ${selectedChart === 'bar' ? 'active' : ''}`}
              onClick={() => setSelectedChart('bar')}
            >
              <FaChartLine /> Bar Chart
            </button>
            <button
              className={`amboss1_chart_type_btn ${selectedChart === 'pie' ? 'active' : ''}`}
              onClick={() => setSelectedChart('pie')}
            >
              <FaChartLine /> Pie Chart
            </button>
            <button
              className={`amboss1_chart_type_btn ${selectedChart === 'line' ? 'active' : ''}`}
              onClick={() => setSelectedChart('line')}
            >
              <FaChartLine /> Line Chart
            </button>
          </div>
          <div className="amboss1_chart_container">
            <h3>AMBOSS Progress Visualization</h3>
            {renderChart(qbankData)}
          </div>
        </div>
      </div>
      
      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default AmbossQBank;
